import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { ProjectDetails, SiteDetailsTab, StakeholderDetails } from '@components/siteSummaryDetails/components';
import { useSiteAllDetailsStore } from '@store/siteAllDetailsStore';
import { useFormik } from 'formik';
import { initialFormikSiteDetails } from '@components/siteSummaryDetails/index';
import { useGetAllSiteDetails } from '@services/hooks/all-site-details';
import { Wrapper } from '@components/common/Wrapper/wrapper';
import { typeOfContractor } from '@config/constant';
import { SiteVisits } from '@components/siteSummaryDetails/components/siteVisits';
import useSiteLeadStore from '@store/siteLeadStore';
import useStepperStore from '@store/stepperStore';
import { useNavigate } from 'react-router-dom';
import { SiteLeadOverStateProps } from '@components/SiteLeadOverview';
import { parseMultiselectOptions } from '@components/delaerVisit/components/addEditDealerVisit';
import { substratesOptions } from '@components/siteLeads/components/addNewSite';
import { useUpdateSiteLeadMutation } from '@services/hooks/site-leads/useUpdateSiteLeadMutation';
import { UpdateSiteLeadData } from '@services/hooks/site-leads';
import { ToastType, useTosterStore } from '@store/toster';
import { RoleType, parseSecondaryCompetitionReasonFromProposedField } from '@helper/utils';
import { useGetRxDb } from '@hooks/getRxdbData';
import { ProfileData } from '@services/hooks/profile';
import { useValidation } from '@hooks/validations';
import { useCompetitionConversionUsageQuery } from '@services/hooks/competition-conversion/useCompetitionConversionUsageQuery';
import { Modal } from '@components/common/modal/modal';
import { SiteVisitRecordingConsentModal } from '@components/siteLeads/siteVisitRecording/SiteVisitRecordingConsentModal';
import { isSiteVisitRecordingActive, useSiteVisitRecordingStore } from '@store/siteVisitRecordingStore';
import { useSiteVisitRecordingUploadStore } from '@store/siteVisitRecordingUploadStore';
import { useIsImpersonationLoginUser } from '../../authGuard/hooks/useIsImpersonationLoginUser';

export const SiteSummaryPageDetails = (): ReactElement => {
	const { currentActiveTab, siteLeadId, clearSiteDetails } = useSiteAllDetailsStore();
	const navigate = useNavigate();
	const { setCurrentStep } = useStepperStore();
	const { setSiteSfid, setSiteLeadId, setSiteVisitId, setKeyBaseValue } = useSiteLeadStore();
	const { data: siteDetail, isFetching, error } = useGetAllSiteDetails(siteLeadId);
	const { mutate: updateSiteLead, isPending: isSiteLeadPending } = useUpdateSiteLeadMutation();
	const { setToaster } = useTosterStore();
	const { SiteSummaryValidationSchema } = useValidation();

	const formik = useFormik({
		initialValues: initialFormikSiteDetails,
		onSubmit: () => {},
		validationSchema: SiteSummaryValidationSchema,
	});
	const { result: profile } = useGetRxDb<ProfileData>('profile');
	const role = profile[0]?.usertype_c;
	const { data: competitionList } = useCompetitionConversionUsageQuery();
	const [isRecordingConsentOpen, setIsRecordingConsentOpen] = useState(false);
	const [pendingDraftVisitId, setPendingDraftVisitId] = useState<number | undefined>();
	const recordingPhase = useSiteVisitRecordingStore((s) => s.phase);
	const clearPendingRecording = useSiteVisitRecordingUploadStore((s) => s.clearPendingRecording);
	const isImpersonationLoginUser = useIsImpersonationLoginUser();

	const competitionOptions = useMemo(() => {
		if (!competitionList?.length) return [];
		return competitionList.map((competition, index) => {
			return {
				id: index,
				value: competition.name,
			};
		});
	}, [competitionList]);

	const secondaryCompetitionOptions = useMemo(() => {
		const options = competitionOptions.length > 0 ? [...competitionOptions] : [];
		// Add "Other" option at the end
		options.push({
			id: competitionOptions.length,
			value: 'Other',
		});
		return options;
	}, [competitionOptions]);

	useEffect(() => {
		if (siteDetail) {
			// Parse proposed_field_competition_sites to auto-fill secondaryCompetitionReason
			const parsedSecondaryReason = parseSecondaryCompetitionReasonFromProposedField(
				siteDetail.proposed_field_competition_sites,
				secondaryCompetitionOptions
			);

			formik.setValues({
				name: siteDetail.name,
				site_location_c: siteDetail.site_location_c,
				grade_glue_used_at_the_site: siteDetail.grade_glue_used_at_the_site,
				brand_of_glue_used_at_site: siteDetail.brand_of_glue_used_at_site ?? '',
				contract_name: siteDetail.contract_name ?? siteDetail.lead_contractor_name,
				contract_phone: siteDetail.contract_phone ?? siteDetail.lead_contractor_phone,
				type_of_contract: typeOfContractor.find((item) => item.name === siteDetail.type_of_contract)?.value ?? '',
				reason_of_competition: siteDetail.reason_of_competition,
				glue_used_at_the_site: siteDetail.glue_used_at_the_site,
				site_type: siteDetail.site_type,
				dealer_code: siteDetail.dealer_code,
				dealer_name: `${siteDetail.dealer_code} - ${siteDetail.dealer_name}`,
				glue_potential: siteDetail.glue_potential,
				architect_name: siteDetail.architect_name,
				architect_mobile_no: siteDetail.architect_mobile_no,
				owner_name: siteDetail.owner_name,
				owner_mobile_no: siteDetail.owner_mobile_no,
				owner_details: [{ id: 0, isActive: true, name: siteDetail.owner_name, number: siteDetail.owner_mobile_no }],
				architect_details: [
					{ id: 0, isActive: true, name: siteDetail.architect_name, number: siteDetail.architect_mobile_no },
				],
				substratesCombinations: parseMultiselectOptions(siteDetail.roff_substrate_c, substratesOptions),
				veneer: siteDetail.epoxy_adhesive_used_c,
				secondaryCompetitionReason: parsedSecondaryReason.options,
				secondaryCompetitionReasonOther: parsedSecondaryReason.otherText ?? '',
				builder_name: siteDetail.builder_name ?? '',
				builder_phone_number: siteDetail.builder_phone_number ?? '',
				builder_office_address: siteDetail.builder_office_address ?? '',
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [siteDetail, secondaryCompetitionOptions]);

	// Clear "Other" text field when "Other" option is deselected
	useEffect(() => {
		const hasOther = formik.values.secondaryCompetitionReason.some((item) => item.value === 'Other');
		if (!hasOther && formik.values.secondaryCompetitionReasonOther) {
			formik.setFieldValue('secondaryCompetitionReasonOther', '');
		}
	}, [formik.values.secondaryCompetitionReason, formik]);

	const contractorDetails = useMemo((): SiteLeadOverStateProps => {
		return {
			name: siteDetail?.contract_name ?? siteDetail?.lead_contractor_name ?? '',
			phone: Number(siteDetail?.contract_phone ?? siteDetail?.lead_contractor_phone) || null,
			number: Number(siteDetail?.contract_phone ?? siteDetail?.lead_contractor_phone) || null,
			sfid: siteDetail?.end_user_c ?? '',
			membershipno: siteDetail?.membership_no ?? '',
			siteId: siteDetail?.id ?? null,
		};
	}, [siteDetail]);
	const proceedToAddNewVisit = (autoStartRecording = true): void => {
		clearPendingRecording();
		clearSiteDetails();
		setCurrentStep(0);
		setKeyBaseValue('addNewSiteLead', false);
		setSiteLeadId(siteDetail?.id ?? null);
		setSiteSfid(siteDetail?.sfid ?? '');
		setKeyBaseValue('siteDetails', {
			name: siteDetail?.contract_name ?? siteDetail?.lead_contractor_name ?? '',
			number: Number(siteDetail?.contract_phone ?? siteDetail?.lead_contractor_phone) || null,
			sfid: siteDetail?.end_user_c ?? '',
			membershipno: siteDetail?.membership_no ?? '',
			lead_membership_no: siteDetail?.lead_membership_no ?? '',
			contractorclass: siteDetail?.contractorclass,
		});
		const navigationState = {
			siteId: siteDetail?.id ?? null,
			...(autoStartRecording ? { autoStartRecording: true as const } : {}),
		};
		if (role === RoleType.KAESS) {
			navigate('/add-new-site-ss', { state: navigationState });
			return;
		}
		navigate('/add-new-site', { state: navigationState });
	};

	const handleAddNewVisitClick = (): void => {
		if (isSiteVisitRecordingActive(recordingPhase)) {
			proceedToAddNewVisit(false);
			return;
		}
		if (siteDetail?.consent) {
			setPendingDraftVisitId(undefined);
			setIsRecordingConsentOpen(true);
		} else {
			proceedToAddNewVisit(false);
		}
	};

	const proceedToDraftSiteVisit = (visitId?: number, autoStartRecording = true): void => {
		clearPendingRecording();
		clearSiteDetails();
		setCurrentStep(2);
		setKeyBaseValue('addNewSiteLead', false);
		setSiteLeadId(siteDetail?.id ?? null);
		setSiteVisitId(visitId);
		setSiteSfid(siteDetail?.sfid ?? '');
		setKeyBaseValue('siteDetails', {
			name: siteDetail?.contract_name ?? siteDetail?.lead_contractor_name ?? '',
			number: Number(siteDetail?.contract_phone ?? siteDetail?.lead_contractor_phone) || null,
			sfid: siteDetail?.end_user_c ?? '',
			membershipno: siteDetail?.membership_no ?? '',
			lead_membership_no: siteDetail?.lead_membership_no ?? '',
			contractorclass: siteDetail?.contractorclass,
		});
		const navigationState = {
			...contractorDetails,
			siteId: siteDetail?.id ?? null,
			...(autoStartRecording ? { autoStartRecording: true as const } : {}),
		};
		if (role === RoleType.KAESS) {
			navigate('/add-new-site-ss', { state: navigationState });
			return;
		}
		navigate('/add-new-site', { state: navigationState });
	};

	const handleDraftVisitClick = (visitId?: number): void => {
		if (isSiteVisitRecordingActive(recordingPhase)) {
			proceedToDraftSiteVisit(visitId, false);
			return;
		}
		if (siteDetail?.consent) {
			setPendingDraftVisitId(visitId);
			setIsRecordingConsentOpen(true);
		} else {
			proceedToDraftSiteVisit(visitId, false);
		}
	};

	const handleRecordingConsentAcknowledge = (): void => {
		setIsRecordingConsentOpen(false);
		if (pendingDraftVisitId != null) {
			proceedToDraftSiteVisit(pendingDraftVisitId);
			setPendingDraftVisitId(undefined);
			return;
		}
		proceedToAddNewVisit();
	};

	const activeTabElement = useMemo((): ReactElement => {
		switch (currentActiveTab) {
			case 0:
				return <ProjectDetails formik={formik} />;
			case 1:
				return <StakeholderDetails formik={formik} />;
			case 2:
				return (
					<SiteVisits
						visits={siteDetail?.visits}
						siteLeadId={siteDetail?.id}
						contractorDetails={contractorDetails}
						onDraftVisitClick={handleDraftVisitClick}
					/>
				);
			default:
				return <></>;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [contractorDetails, currentActiveTab, formik, siteDetail?.visits]);

	const handleSubmit = useCallback(async (): Promise<void> => {
		formik.setSubmitting(true);

		const errors = await formik.validateForm();
		formik.setTouched({
			architect_details: formik.values.architect_details.map(() => ({ name: true, number: true })),
			owner_details: formik.values.owner_details.map(() => ({ name: true, number: true })),
		});

		if (Object.keys(errors).length > 0) {
			setToaster(true, ToastType.error, 'Please fill all required fields correctly.');
			return;
		}
		const payload: UpdateSiteLeadData = {
			site_type: formik.values.site_type,
			dealer_name: formik.values.dealer_name.split('-')[1].trim(),
			dealer_code: formik.values.dealer_name.split('-')[0].trim(),
			glue_potential: formik.values.glue_potential,
			architect_name: formik.values.architect_details[0].name,
			architect_mobile_no: formik.values.architect_details[0].number,
			owner_name: formik.values.owner_details[0].name,
			owner_mobile_no: formik.values.owner_details[0].number,
			epoxy_adhesive_used_c: formik.values.veneer,
			...(siteDetail?.end_user_c !== null && siteDetail?.end_user_c !== undefined
				? { end_user_c: siteDetail?.end_user_c }
				: { lead_app_id_c: siteDetail?.lead_app_id_c }),
		};
		updateSiteLead?.(payload, {
			onSuccess: () => {
				setToaster(true, ToastType.success, 'Stackholder details updated successfully.');
			},
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [formik.values, updateSiteLead]);

	const submitButton = (): ReactElement | null => {
		if (currentActiveTab === 2) {
			return (
				<div className="submit-btn">
					<button
						type="button"
						className="btn btn-medium btn-primary"
						id="add-new-visit-btn"
						disabled={siteDetail?.current_stage_of_site_c === 'Rejected' || isImpersonationLoginUser}
						onClick={handleAddNewVisitClick}
					>
						Add New Visit
					</button>
				</div>
			);
		}
		if (currentActiveTab === 1) {
			return (
				<div className="submit-btn">
					<button
						type="button"
						disabled={
							isSiteLeadPending || siteDetail?.current_stage_of_site_c === 'Rejected' || isImpersonationLoginUser
						}
						onClick={handleSubmit}
						className="btn btn-medium btn-primary"
						id="edit-btn"
					>
						Edit
					</button>
				</div>
			);
		}

		return null;
	};

	return (
		<>
			<section className="site-summary-all-details">
				<div className="container">
					<SiteDetailsTab />
					<Wrapper isLoaderVisible={isFetching} isError={error}>
						<div className="summary-details-form">
							<form>{activeTabElement}</form>
							{role !== RoleType.RBDM && submitButton()}
						</div>
					</Wrapper>
				</div>
			</section>
			<Modal
				modalOpen={isRecordingConsentOpen}
				modalId="siteVisitRecordingInfo"
				onModalClose={() => setIsRecordingConsentOpen(false)}
				className="dialog-sm opt-confirm pt-25"
			>
				<SiteVisitRecordingConsentModal
					onAcknowledge={handleRecordingConsentAcknowledge}
					onDismiss={() => {
						setIsRecordingConsentOpen(false);
						setPendingDraftVisitId(undefined);
					}}
				/>
			</Modal>
		</>
	);
};
